import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Container, Spinner } from "@/components/shared";
import { ArrowLeft, MessageSquare, Quote as QuoteIcon, BookOpen, Lightbulb, Users, AlertTriangle, Scale } from "lucide-react";

const CONF = {
  green: { dot: "🟢", label: "Consensus historique fort", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5" },
  yellow: { dot: "🟡", label: "Sujet débattu entre chercheurs", cls: "text-amber-400 border-amber-500/30 bg-amber-500/5" },
  red: { dot: "🔴", label: "Hypothèse ou théorie minoritaire", cls: "text-red-400 border-red-500/30 bg-red-500/5" },
};

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="card-surface p-5" data-testid={testid}>
    <h2 className="text-gold text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-3">
      <Icon size={14} /> {title}
    </h2>
    {children}
  </div>
);

export default function AuthorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null); setErr(false);
    api.get(`/afro/authors/${slug}`).then(({ data }) => setA(data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <Container><p className="text-white/60">Fiche introuvable. <Link to="/auteurs" className="text-gold">Retour</Link></p></Container>;
  if (!a) return <Container><Spinner label="Chargement de la fiche..." /></Container>;

  const conf = CONF[a.confidence];

  return (
    <Container className="max-w-3xl">
      <button onClick={() => navigate("/auteurs")} data-testid="author-back"
        className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeft size={16} /> Tous les auteurs
      </button>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="author-detail">
        {/* En-tête */}
        <div className="card-surface p-6">
          <p className="text-white/40 text-[11px] uppercase tracking-[0.2em] mb-2">{a.category}</p>
          <h1 className="font-display text-3xl md:text-4xl gold-text leading-tight" data-testid="author-name">{a.name}</h1>
          <p className="text-white/45 text-sm mt-1">{a.dates} · {a.domain}</p>
          <p className="text-white/75 text-sm leading-relaxed mt-4">{a.bio}</p>
          <div className="flex flex-wrap items-center gap-3 mt-5">
            {conf && (
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${conf.cls}`} data-testid="author-confidence">
                {conf.dot} {conf.label}
              </span>
            )}
            <button onClick={() => navigate(`/reply?comment=${encodeURIComponent("Parle-moi de " + a.name + " et de ses thèses.")}&auto=1`)}
              data-testid="author-ask-ai"
              className="inline-flex items-center gap-2 gold-gradient text-black rounded-full px-4 py-1.5 text-xs font-semibold hover:opacity-90 transition-opacity">
              <MessageSquare size={14} /> Interroger BIZARRE AI
            </button>
          </div>
        </div>

        {a.concepts?.length > 0 && (
          <Section icon={Lightbulb} title="Concepts clés" testid="author-concepts">
            <div className="flex flex-wrap gap-2">
              {a.concepts.map((c, i) => (
                <span key={i} className="rounded-full border border-white/12 bg-white/[0.03] px-3 py-1 text-xs text-white/80">{c}</span>
              ))}
            </div>
          </Section>
        )}

        {a.works?.length > 0 && (
          <Section icon={BookOpen} title="Œuvres principales" testid="author-works">
            <ul className="space-y-1.5">
              {a.works.map((w, i) => (
                <li key={i} className="text-white/80 text-sm flex items-start gap-2"><span className="text-gold/60 mt-1">•</span><span>{w}</span></li>
              ))}
            </ul>
          </Section>
        )}

        {a.citations?.length > 0 && (
          <Section icon={QuoteIcon} title="Citations" testid="author-citations">
            <div className="space-y-3">
              {a.citations.map((c, i) => (
                <p key={i} className="text-white/85 text-sm italic leading-relaxed border-l-2 border-gold/50 pl-3">{c}</p>
              ))}
            </div>
          </Section>
        )}

        {a.books?.length > 0 && (
          <Section icon={BookOpen} title="Livres recommandés" testid="author-books">
            <ul className="space-y-1.5">
              {a.books.map((b, i) => (
                <li key={i} className="text-white/80 text-sm flex items-start gap-2"><span className="text-gold/60 mt-1">•</span><span>{b}</span></li>
              ))}
            </ul>
          </Section>
        )}

        {a.objections?.length > 0 && (
          <Section icon={AlertTriangle} title="Critiques académiques" testid="author-critiques">
            <ul className="space-y-2">
              {a.objections.map((o, i) => (
                <li key={i} className="text-white/70 text-sm leading-relaxed flex items-start gap-2"><span className="text-amber-400/70 mt-1">•</span><span>{o}</span></li>
              ))}
            </ul>
          </Section>
        )}

        {a.state && (
          <Section icon={Scale} title="Niveau de consensus" testid="author-consensus">
            <p className="text-white/80 text-sm leading-relaxed">{a.state}</p>
            {a.source && <p className="text-white/40 text-xs mt-3">Source : {a.source}</p>}
          </Section>
        )}

        {a.related_authors?.length > 0 && (
          <Section icon={Users} title="Auteurs liés" testid="author-related">
            <div className="flex flex-wrap gap-2">
              {a.related_authors.map((r) => (
                <Link key={r.slug} to={`/auteurs/${r.slug}`} data-testid={`author-related-${r.slug}`}
                  className="inline-flex items-center gap-1 rounded-full border border-white/12 px-3 py-1.5 text-xs text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
                  {r.name}
                </Link>
              ))}
            </div>
          </Section>
        )}
      </motion.div>
    </Container>
  );
}
